/**
 * useRateLimitWarning — scans job history across all three modules and
 * returns the most recent rate-limit / auth-error message (or null).
 * Polls every 30 s so the banner updates without page refresh.
 */
import { useEffect, useState } from 'react'
import { reports, competitor, context } from '@/api/client'
import type { HistoryJob } from '@/types'

const RATE_RE = /rate.?limit|insufficient.quota|quota.exceed|⚠.*rate/i
const AUTH_RE = /invalid.api.key|AuthenticationError|⚠.*invalid.api/i

function isKeyError(err?: string): boolean {
  if (!err) return false
  return RATE_RE.test(err) || AUTH_RE.test(err)
}

function latestKeyError(jobs: HistoryJob[]): string | null {
  const hit = jobs
    .filter(j => j.status === 'failed' && isKeyError(j.error))
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]
  return hit?.error ?? null
}

export function useRateLimitWarning() {
  const [warning, setWarning] = useState<string | null>(null)

  const check = async () => {
    try {
      const [rJobs, cJobs, xJobs] = await Promise.allSettled([
        reports.listJobs(),
        competitor.listJobs(),
        context.listJobs(),
      ])

      const all: HistoryJob[] = [
        ...(rJobs.status === 'fulfilled' ? rJobs.value : []),
        ...(cJobs.status === 'fulfilled' ? cJobs.value : []),
        ...(xJobs.status === 'fulfilled' ? xJobs.value : []),
      ]

      setWarning(latestKeyError(all))
    } catch {
      // Silently ignore — don't show errors from the check itself
    }
  }

  useEffect(() => {
    check()
    const timer = setInterval(check, 30_000)
    return () => clearInterval(timer)
  }, [])

  return warning
}
